import { useRef, useCallback, useEffect } from "react";
import { logEvent } from "@shared/analytics/analytics";

/**
 * Hook to track time spent on a multi-step form.
 * - Tracks start time when `startTracking` is called
 * - Allows logging partial times per step
 * - Logs abandonment if user leaves without completing
 */
export function useFormCompletionTracker(formName: string) {
  const startTimeRef = useRef<number | null>(null);
  const lastStepRef = useRef<string | null>(null);

  /**
   * ✅ Call this when form starts (e.g., component mount)
   */
  const startTracking = useCallback(() => {
    startTimeRef.current = Date.now();
    logEvent(`${formName} Form Tracking Started`, {
      formName,
      startTime: new Date().toISOString(),
    });
  }, [formName]);

  /**
   * ✅ Logs time spent until a particular step is reached/saved
   * Accepts stepIndex or a descriptive stepName for better analytics.
   */
  const logTimeUntilStep = useCallback(
    (step: number | string, extraProps?: Record<string, any>) => {
      if (!startTimeRef.current) {
        console.warn("[FormTracker] Tracking not started. Call startTracking() first.");
        return;
      }

      const timeTaken = Date.now() - startTimeRef.current;
      const stepLabel = typeof step === "number" ? `Step ${step}` : step;


      // store last visited step (important for abandonment logging)
      lastStepRef.current = stepLabel;

      logEvent(`${formName} Step Completed`, {
        formName,
        step: stepLabel,
        timeTakenMs: timeTaken,
        timeTakenSec: Math.round(timeTaken / 1000),
        ...(extraProps || {}),
      });
    },
    [formName]
  );

  /**
   * ✅ Call when form is successfully submitted
   * Ends the tracking session and logs total time
   */
  const stopTracking = useCallback(
    (extraProps?: Record<string, any>) => {
      if (!startTimeRef.current) return;

      const totalTime = Date.now() - startTimeRef.current;

      logEvent(`${formName} Form Completed`, {
        formName,
        totalTimeMs: totalTime,
        totalTimeSec: Math.round(totalTime / 1000),
        ...(extraProps || {}),
      });

      // reset after completion
      startTimeRef.current = null;
      lastStepRef.current = null;
    },
    [formName]
  );

  /**
   * ✅ Logs abandonment if component unmounts before `stopTracking()` is called
   * Will include the last step/tab name where user dropped off
   */
  useEffect(() => {
    return () => {
      if (startTimeRef.current) {
        const totalTime = Date.now() - startTimeRef.current;

        logEvent(`${formName} Form Abandoned`, {
          formName,
          lastStep: lastStepRef.current || "Unknown",
          totalTimeMs: totalTime,
          totalTimeSec: Math.round(totalTime / 1000),
        });
      }
    };
  }, [formName]);

  return {
    startTracking,
    logTimeUntilStep,
    stopTracking,
  };
}
